/* eslint-disable */
/**
 * GENERATED FILE — DO NOT EDIT BY HAND.
 *
 * Source:     functions/lib/serviceHours.js
 * Generator:  scripts/generateServiceHours.js
 * Regenerate: npm run gen:service-hours   (from melio-app/)
 *
 * Melio's published service hours. The Abnormal Results Pathway's clocks read the
 * source directly; this mirror is what the member app shows and what the messaging
 * interface keys its out-of-hours state off, so the two cannot disagree about
 * whether Melio is open. engine-tests/service-hours-sync.test.js fails the build if
 * they drift.
 *
 * ⚠️ THE HOURS ARE A DRAFT pending Medical Director confirmation, like the rest of
 *    the pathway's working-draft values. They are published to members, so
 *    confirming them is a real task rather than a formality.
 */

export interface ServiceHours {
  timeZone: string;
  /** ISO weekday numbers: 1 = Monday … 7 = Sunday. */
  days: number[];
  openMinutes: number;
  closeMinutes: number;
}

export const SERVICE_HOURS: ServiceHours = {
  timeZone: "Europe/London",
  days: [1,2,3,4,5],
  openMinutes: 540,
  closeMinutes: 1050,
};

export const MODELS_BANK_HOLIDAYS = true;

/**
 * England & Wales bank holidays, mirrored from the source so the app and the
 * pathway's clocks agree about what a service day is.
 *
 * Past BANK_HOLIDAY_CALENDAR_THROUGH the table runs out and every weekday reads as
 * a service day again — which here means telling a member Melio is open when it is
 * not. Extend the table in functions/lib/serviceHours.js before that date.
 */
export const BANK_HOLIDAYS: ReadonlySet<string> = new Set(["2026-01-01","2026-04-03","2026-04-06","2026-05-04","2026-05-25","2026-08-31","2026-12-25","2026-12-28","2027-01-01","2027-03-26","2027-03-29","2027-05-03","2027-05-31","2027-08-30","2027-12-27","2027-12-28","2028-01-03","2028-04-14","2028-04-17","2028-05-01","2028-05-29","2028-08-28","2028-12-25","2028-12-26"]);

export const BANK_HOLIDAY_CALENDAR_THROUGH = "2028-12-31";

function isoDate(parts: { year: number; month: number; day: number }): string {
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

/** How the hours read to a member. Used in the app, the terms and the messaging UI. */
export const SERVICE_HOURS_LABEL = "Mon–Fri, 09:00–17:30 UK time";
export const SERVICE_DAYS_LABEL = "Mon–Fri";
export const SERVICE_OPEN_LABEL = "09:00";
export const SERVICE_CLOSE_LABEL = "17:30";

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7,
};

const PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: SERVICE_HOURS.timeZone,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

interface LondonParts {
  year: number; month: number; day: number;
  hour: number; minute: number; weekday: number;
}

/**
 * London wall-clock parts for an instant.
 *
 * Reads the London clock rather than the browser's, because a member in Dubai is
 * served by a UK service: whether Melio is open must not change with where they
 * happen to be standing.
 */
function londonParts(at: Date): LondonParts {
  const raw: Record<string, string> = {};
  PARTS.formatToParts(at).forEach((p) => { raw[p.type] = p.value; });
  return {
    year: Number(raw.year),
    month: Number(raw.month),
    day: Number(raw.day),
    // Intl renders midnight as '24' in some locales; normalise it.
    hour: Number(raw.hour) % 24,
    minute: Number(raw.minute),
    weekday: WEEKDAY_INDEX[raw.weekday],
  };
}

function isServiceDay(parts: LondonParts): boolean {
  return SERVICE_HOURS.days.includes(parts.weekday) && !BANK_HOLIDAYS.has(isoDate(parts));
}

/** Mirrors isWithinServiceHours() in the source module. */
export function isWithinServiceHours(at: Date = new Date()): boolean {
  const parts = londonParts(at);
  if (!isServiceDay(parts)) return false;
  const minutes = (parts.hour * 60) + parts.minute;
  return minutes >= SERVICE_HOURS.openMinutes && minutes < SERVICE_HOURS.closeMinutes;
}

/** ISO weekday (1–7) in London for an instant, for callers that need the day. */
export function londonWeekday(at: Date = new Date()): number {
  return londonParts(at).weekday;
}

/**
 * When Melio next opens, in words — "this morning", "tomorrow morning",
 * "Monday morning".
 *
 * Deliberately vague about the minute: the pathway says messages are "read at the
 * next service period", not answered at 09:00 sharp.
 *
 * Walks real calendar days rather than weekday arithmetic, because a bank holiday
 * has to be skipped by date and "Monday" may not be the answer.
 */
export function nextOpeningLabel(at: Date = new Date()): string {
  const today = londonParts(at);
  const minutes = (today.hour * 60) + today.minute;
  if (isServiceDay(today) && minutes < SERVICE_HOURS.openMinutes) return 'this morning';

  const DAY_MS = 24 * 60 * 60 * 1000;
  for (let i = 1; i <= 21; i += 1) {
    const candidate = londonParts(new Date(at.getTime() + (i * DAY_MS)));
    if (!isServiceDay(candidate)) continue;
    if (i === 1) return 'tomorrow morning';
    return `${DAY_NAMES[candidate.weekday]} morning`;
  }
  return 'the next working day';
}

const DAY_NAMES: Record<number, string> = {
  "1": "Monday",
  "2": "Tuesday",
  "3": "Wednesday",
  "4": "Thursday",
  "5": "Friday",
  "6": "Saturday",
  "7": "Sunday"
};
